import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonPseudo,
  SidePanelLoader,
  ajvResolver,
  InputText,
  PermissionEnum,
  Select,
  responseErrorsResolver,
  ResponseError,
  Popup,
  PermissionDenied,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearIncorporationDateFormState } from '../store/slices/incorporation-dates-form';
import { RiskScoreItem } from '../../types';
import { RiskScoreRange as RiskScoreRangeValues } from '../../../store/dictionaries/types';
import {
  useLazyGetIncorporationDateQuery,
  useCreateIncorporationDateMutation,
  useUpdateIncorporationDateMutation,
  useDeleteIncorporationDateMutation,
  useLazyGetIncorporationDatesQuery,
} from '../store/services';
import getRiskScore from '../../helpers/get-risk-score';

import ROUTES from '../../../routes';

import cn from '../corporate-clients-risk.module.css';

let timeout: NodeJS.Timeout;

export const schema = {
  type: 'object',
  properties: {
    riskScore: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Risk Score is required.',
      },
    },
    fromYear: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Year is required.',
      },
    },
    riskScoreRangeId: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Risk Score Range is required.',
      },
    },
  },
  required: ['riskScore', 'fromYear', 'riskScoreRangeId'],
};

const IncorporationDateForm = () => {
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [defaultValues, setDefaultValues] = useState({
    fromYear: '',
    riskScoreRangeId: '',
    riskScore: '',
  });
  const [globalError, setGlobalError] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
    watch,
  } = useForm<{
    fromYear: string;
    riskScoreRangeId: string;
    riskScore: string;
  }>({
    resolver: ajvResolver(schema),
    defaultValues: {
      fromYear: '',
      riskScoreRangeId: '',
      riskScore: '',
    },
  });

  const fromYear = watch('fromYear');
  const riskScore = watch('riskScore');
  const riskScoreRangeId = watch('riskScoreRangeId');

  const {
    corporateClientsRisk: {
      incorporationDates: {
        form: { item },
      },
    },
    amlDictionaries: { activePermissions, riskScoreRanges },
  } = useAppSelector((state) => state);

  const isDirty = !isEqual(defaultValues, {
    fromYear,
    riskScore,
    riskScoreRangeId,
  });

  const { dateId } = useParams();
  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [create, { isLoading: requestInProgress, isSuccess: submitSuccess }] =
    useCreateIncorporationDateMutation();
  const [update, { isLoading: updateInProgress, isSuccess: updateSuccess }] =
    useUpdateIncorporationDateMutation();
  const [getList] = useLazyGetIncorporationDatesQuery();
  const [
    deleteItem,
    { isLoading: deleteInProgress, isSuccess: deleteIsSuccess },
  ] = useDeleteIncorporationDateMutation();

  const onFormSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    let response;

    const body = {
      riskScore: Number(values.riskScore),
      riskScoreRangeId: values.riskScoreRangeId,
      fromYear: Number(values.fromYear),
    };

    if (dateId) {
      response = (await update({
        id: dateId,
        body,
      })) as {
        data?: RiskScoreItem;
        error: ResponseError;
      };
    } else {
      response = (await create(body)) as {
        data?: RiskScoreItem;
        error: ResponseError;
      };
    }

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    } else if (!response?.error && !dateId && getList) {
      getList('?take=30');
    }
  });

  const onDelete = async () => {
    const response = (await deleteItem(dateId as string)) as {
      error: ResponseError;
    };

    if (!response?.error) {
      getList('?take=30');
    }
  };

  const [getIncorporationDate, { isLoading, isFetching }] =
    useLazyGetIncorporationDateQuery();

  const getIncorporationDateAction = useCallback(() => {
    getIncorporationDate(dateId as string);
  }, [dateId]);

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(
        `${ROUTES.CORPORATE_CLIENTS_RISK_DATE_OF_INCORPORATION}${search || ''}`,
      );
    }, 100);
  }, []);

  useEffect(() => {
    if (submitSuccess) {
      handleClose();
    }
  }, [submitSuccess]);

  useEffect(() => {
    if (updateSuccess) {
      setDefaultValues({
        fromYear,
        riskScore,
        riskScoreRangeId,
      });

      handleClose();
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (deleteIsSuccess) {
      handleClose();
    }
  }, [deleteIsSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (dateId) {
      getIncorporationDateAction();
    }

    return () => {
      dispatch(clearIncorporationDateFormState());
    };
  }, [dispatch, dateId, getIncorporationDateAction]);

  useEffect(() => {
    if (item) {
      reset({
        fromYear: item?.fromYear.toString(),
        riskScore: item?.riskScore.toString(),
        riskScoreRangeId: item.riskScoreRangeId,
      });

      setDefaultValues({
        fromYear: item?.fromYear.toString(),
        riskScore: item?.riskScore.toString(),
        riskScoreRangeId: item.riskScoreRangeId,
      });
    }
  }, [reset, item]);

  let content = (
    <div className={cn.incorporationDateForm}>
      <div className={cn.incorporationDateFormRow}>
        <Controller
          control={control}
          name="fromYear"
          render={({ field: { onChange, value } }) => (
            <InputText
              name="fromYear"
              placeholder=""
              onChange={onChange}
              value={value}
              title="Number of years"
              error={errors?.fromYear?.message}
              className={cn.incorporationDatesInput}
              labelClassName={`${cn.incorporationDatesInputLabel} ${
                !activePermissions?.includes(
                  PermissionEnum.SETTINGS_INCORPORATION_DATES_EDIT_ACTION,
                )
                  ? cn.incorporationDatesInputLabelDisabled
                  : ''
              }`}
              disabled={
                !activePermissions?.includes(
                  PermissionEnum.SETTINGS_INCORPORATION_DATES_EDIT_ACTION,
                )
              }
              prefix="From"
              suffix="Year"
              prefixClassName={cn.incorporationDatesPrefix}
              suffixClassName={cn.incorporationDatesSuffix}
            />
          )}
        />
      </div>
      <div className={cn.incorporationDateFormRow}>
        <Controller
          control={control}
          name="riskScore"
          render={({ field: { onChange, value } }) => (
            <InputText
              name="riskScore"
              placeholder=""
              onChange={(e) => {
                clearTimeout(timeout);

                onChange(e.target.value);

                timeout = setTimeout(() => {
                  const currentRange = getRiskScore({
                    value: e.target.value,
                    riskScoreRanges: riskScoreRanges as RiskScoreRangeValues[],
                  });

                  if (setValue) {
                    setValue('riskScoreRangeId', currentRange as string);
                  }
                }, 300);
              }}
              value={value}
              title="Risk score"
              disabled={
                !activePermissions?.includes(
                  PermissionEnum.SETTINGS_INCORPORATION_DATES_EDIT_ACTION,
                )
              }
              error={errors?.riskScore?.message}
            />
          )}
        />
      </div>
      <div className={cn.incorporationDateFormRow}>
        <Controller
          control={control}
          name="riskScoreRangeId"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <>
              <Select
                list={riskScoreRanges}
                placeholder="Choose risk score range"
                name={name}
                title="Risk Score Range"
                value={value}
                onChange={(e) => {
                  onChange(e.target.value);

                  const currentRange = riskScoreRanges?.find(
                    (range) => range.value === e.target.value,
                  );

                  if (currentRange) {
                    setValue('riskScore', currentRange.from.toString());
                  }
                }}
                selectWrapperClassName={`${cn.riskScoreSelect} ${
                  errors?.riskScoreRangeId?.message
                    ? cn.riskScoreSelectError
                    : ''
                }`}
                disabled={
                  !activePermissions?.includes(
                    PermissionEnum.SETTINGS_INCORPORATION_DATES_EDIT_ACTION,
                  )
                }
                fullWidth
              />
              {errors?.riskScoreRangeId?.message && (
                <div className={cn.error}>
                  {errors?.riskScoreRangeId?.message}
                </div>
              )}
            </>
          )}
        />
      </div>
    </div>
  );

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (
    !isLoading &&
    !dateId &&
    !activePermissions?.includes(
      PermissionEnum.SETTINGS_INCORPORATION_DATES_CREATE_ACTION,
    )
  ) {
    content = <PermissionDenied />;
  }

  let title = 'Add range of years';

  if (
    dateId &&
    activePermissions?.includes(
      PermissionEnum.SETTINGS_INCORPORATION_DATES_EDIT_ACTION,
    )
  ) {
    title = 'Edit range of years';
  } else if (
    dateId &&
    !activePermissions?.includes(
      PermissionEnum.SETTINGS_INCORPORATION_DATES_EDIT_ACTION,
    )
  ) {
    title = 'Information about range of years';
  }

  let saveButton;

  if (
    (dateId &&
      activePermissions?.includes(
        PermissionEnum.SETTINGS_INCORPORATION_DATES_EDIT_ACTION,
      )) ||
    (!dateId &&
      activePermissions?.includes(
        PermissionEnum.SETTINGS_INCORPORATION_DATES_CREATE_ACTION,
      ))
  ) {
    saveButton = (
      <ButtonPrimary
        text={requestInProgress || updateInProgress ? 'Loading...' : 'Save'}
        disabled={requestInProgress || updateInProgress || deleteInProgress}
        onClick={onFormSubmit}
        bold
      />
    );
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={title}
        errorSidepanel={globalError}
        sidepanelExpandedKey="incorporation-date-panel"
        footer={
          <>
            {saveButton}
            {dateId &&
              activePermissions?.includes(
                PermissionEnum.SETTINGS_INCORPORATION_DATES_DELETE_ACTION,
              ) && (
                <ButtonPrimary
                  text={deleteInProgress ? 'Loading...' : 'Delete'}
                  className={cn.deleteButton}
                  onClick={onDelete}
                  disabled={updateInProgress || deleteInProgress}
                  bold
                />
              )}
            <ButtonPseudo
              text="Cancel"
              className="button-white"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default IncorporationDateForm;
